// [목적] ApiError 분기 처리 class 기반 ErrorBoundary — 401/403/404/429/5xx 표준 메시지 + fallback UI
// [이유] FEED-07 AC-6 — TanStack Query 의 throwOnError 또는 명시적 throw 로 올라오는 에러를
//         컴포넌트 트리 상단에서 잡아 사용자에게 적절한 메시지를 보여주고 상위 레이아웃 붕괴 방지
// [사이드 이팩트] ApiError(status, code, message) 구조에 의존 — http.ts ApiError 클래스와 동기화 필요.
//                 props.onReset 이 없으면 기본 "다시 시도" 버튼으로 this.setState({error: null}) 재마운트
// [수정 시 고려사항] React 18 class ErrorBoundary — getDerivedStateFromError + componentDidCatch 패턴 유지.
//                   TanStack Query v5 에서 useErrorBoundary 대신 throwOnError 옵션 사용 (동일 결과).
//                   로그인 유도 CTA 는 guest 여부를 props 로 주입받아 분기 (AuthStatus 직접 import 금지 — 순환 의존 방지)

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { ApiError } from '../../api/http.js';

// tokens 는 위 import 에서 직접 사용 — 파일 하단 중복 import 없음

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER1 = c.paper[1][L];
const BRAND = c.brand[500][L];
const DANGER = c.danger[500][L];

// ─── 에러 코드 → 사용자 메시지 매핑 ──────────────────────────────────────────

/**
 * [목적] HTTP 상태 코드 기준 사용자 친화 메시지 반환
 * [이유] 에러 코드별 안내 문구 일원화 — 컴포넌트마다 별도 메시지 작성 방지
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] BE 가 추가 에러 코드를 도입 시 여기에 케이스 추가
 */
function resolveMessage(error: unknown): { title: string; desc: string; isAuthError: boolean } {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return {
          title: '로그인이 필요합니다',
          desc: '이 페이지를 보려면 로그인해야 합니다.',
          isAuthError: true,
        };
      case 403:
        return {
          title: '접근 권한이 없습니다',
          desc: '이 콘텐츠는 공개되지 않거나 권한이 없는 콘텐츠입니다.',
          isAuthError: false,
        };
      case 404:
        return {
          title: '찾을 수 없습니다',
          desc: '요청하신 콘텐츠가 존재하지 않거나 삭제되었습니다.',
          isAuthError: false,
        };
      case 429:
        return {
          title: '잠시 후 다시 시도해 주세요',
          desc: '요청이 너무 많습니다. 잠시 기다린 후 다시 시도해 주세요.',
          isAuthError: false,
        };
      default:
        if (error.status >= 500) {
          return {
            title: '서버 오류가 발생했습니다',
            desc: '일시적인 오류입니다. 잠시 후 다시 시도해 주세요.',
            isAuthError: false,
          };
        }
        return {
          title: '오류가 발생했습니다',
          desc: error.message || '알 수 없는 오류가 발생했습니다.',
          isAuthError: false,
        };
    }
  }
  return {
    title: '예기치 않은 오류',
    desc: '잠시 후 다시 시도해 주세요.',
    isAuthError: false,
  };
}

// ─── 상태 타입 ──────────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  error: unknown | null;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** 리셋 콜백 — 없으면 내부 setState 재마운트 */
  onReset?: () => void;
  /** guest 여부 — true 이면 401/403 fallback 에 로그인 CTA 표시 */
  isGuest?: boolean;
  /** 로그인 경로 — 기본값 '/login' */
  loginHref?: string;
  /** 커스텀 fallback UI (error 주입) */
  fallback?: (error: unknown, reset: () => void) => ReactNode;
}

// ─── ErrorBoundary ──────────────────────────────────────────────────────────

/**
 * [목적] React class ErrorBoundary — ApiError 분기 fallback UI
 * [이유] TanStack Query throwOnError / 컴포넌트 내부 throw 를 트리 상단에서 캐치
 * [사이드 이팩트] componentDidCatch 에서 console.error 로깅 (프로덕션 에러 모니터링 연계 가능)
 * [수정 시 고려사항] React 18 에서 class 기반만 ErrorBoundary 구현 가능. Hook 으로 대체 불가
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // 에러 모니터링 연계 지점 (Sentry 등 후속 추가 가능)
    console.error('[ErrorBoundary] caught:', error, info.componentStack);
  }

  /**
   * [목적] 에러 상태 초기화 — 재마운트 트리거
   * [이유] onReset 없을 때 내부에서 setState 로 재시도 허용
   * [사이드 이팩트] error=null → 자식 트리 재렌더링
   * [수정 시 고려사항] 외부 onReset 과 병행 가능
   */
  private handleReset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback, isGuest, loginHref = '/login' } = this.props;

    if (error === null) return children;

    // 커스텀 fallback 우선
    if (fallback) return fallback(error, this.handleReset);

    const { title, desc, isAuthError } = resolveMessage(error);
    const showLoginCTA = isAuthError && isGuest;

    return (
      <Box
        role="alert"
        aria-live="assertive"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: '48px 20px', md: '72px 32px' },
          textAlign: 'center',
          minHeight: 240,
          bgcolor: PAPER1,
          borderRadius: `${tokens.radius.l}px`,
          border: `1px solid ${BORDER}`,
          m: { xs: '16px', md: '24px' },
        }}
      >
        {/* 상태 아이콘 */}
        <Box
          aria-hidden="true"
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: error instanceof ApiError && error.status === 404 ? PAPER1 : `${DANGER}12`,
            border: `1.5px solid ${error instanceof ApiError && error.status === 404 ? BORDER : `${DANGER}40`}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            mb: '16px',
          }}
        >
          {error instanceof ApiError
            ? error.status === 404
              ? '∅'
              : error.status === 401 || error.status === 403
                ? '⊘'
                : '!'
            : '!'}
        </Box>

        <Typography
          component="h2"
          sx={{ fontSize: 16, fontWeight: 700, color: INK_HI, mb: '8px', letterSpacing: '-0.3px' }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: INK_MID, lineHeight: 1.6, mb: '20px', maxWidth: 360 }}
        >
          {desc}
        </Typography>

        {/* 로그인 CTA (401/403 + guest) */}
        {showLoginCTA && (
          <ButtonBase
            component="a"
            href={loginHref}
            aria-label="로그인 페이지로 이동"
            sx={{
              bgcolor: BRAND,
              color: '#fff',
              borderRadius: `${tokens.radius.s}px`,
              px: '20px',
              py: '10px',
              fontSize: 13,
              fontWeight: 700,
              mb: '8px',
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            로그인하러 가기
          </ButtonBase>
        )}

        {/* 재시도 버튼 */}
        {!showLoginCTA && (
          <ButtonBase
            onClick={this.handleReset}
            aria-label="다시 시도"
            sx={{
              bgcolor: BRAND,
              color: '#fff',
              borderRadius: `${tokens.radius.s}px`,
              px: '20px',
              py: '10px',
              fontSize: 13,
              fontWeight: 700,
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            다시 시도
          </ButtonBase>
        )}

        {/* 부가 코드 표시 (개발/디버깅용 — 프로덕션에서 숨길 경우 env 분기 가능) */}
        {error instanceof ApiError && (
          <Typography
            sx={{
              mt: '16px',
              fontSize: 10.5,
              color: INK_LOW,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {error.status} · {error.code}
          </Typography>
        )}
      </Box>
    );
  }
}
